import React from "react";
import { ChartDataItem } from "../../types/dashboard";
import { useFormatAmount } from "../../../home/hooks/useFormatAmount";
import { useUser } from "src/shared/context/UserContext";

interface DataSummaryProps {
  chartData: ChartDataItem[];
  businessType: string;
  timeRange?: "today" | "yesterday" | "week" | "month";
  receivedLabels?: {
    order: string;
    ledger: string;
    orderShort: string;
    ledgerShort: string;
  };
}

const DataSummary: React.FC<DataSummaryProps> = ({
  chartData,
  businessType,
  timeRange = "today",
  receivedLabels = {
    order: "Today's Order Received",
    ledger: "Outstanding Ledger Received",
    orderShort: "Today's Received",
    ledgerShort: "Outstanding Received",
  },
}) => {
  const formatAmount = useFormatAmount();
  const { user } = useUser();

  const isRestaurant = user?.businessType === "Restaurant" || businessType === "Restaurant";

  // Filter out Outstanding for Restaurant
  let filteredChartData = isRestaurant ? chartData.filter((item) => item.name !== "Outstanding") : chartData;

  // Find received items using the provided labels
  const orderReceived = filteredChartData.find((item) => item.name === receivedLabels.order);
  const ledgerReceived = filteredChartData.find((item) => item.name === receivedLabels.ledger);

  // Remove received items from main list
  const otherItems = filteredChartData.filter((item) => item.name !== receivedLabels.order && item.name !== receivedLabels.ledger);

  // Calculate total for percentages (use 1 to avoid division by zero)
  const totalWithReceived = filteredChartData.reduce((sum, item) => sum + item.value, 0) || 1;

  // Calculate total received
  const totalReceived = (orderReceived?.value || 0) + (ledgerReceived?.value || 0);

  // Separate Outstanding from other items
  const outstandingItem = otherItems.find((item) => item.name === "Outstanding");
  const itemsWithoutOutstanding = otherItems.filter((item) => item.name !== "Outstanding");

  return (
    <div className="space-y-3">
      {/* Other Items Grid (excluding Outstanding) */}
      {itemsWithoutOutstanding.length && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-2">
          {itemsWithoutOutstanding.map((entry, index) => {
            const percentage = (entry.value / totalWithReceived) * 100;
            const displayValue = entry.name === "Orders" ? entry.value.toLocaleString() : formatAmount(entry.value);

            return (
              <div
                key={`detail-${index}`}
                className={`group relative overflow-hidden rounded-xl bg-gradient-to-br from-white to-gray-50/80 border p-2 transition-all duration-200 ${
                  entry.value === 0 ? "border-gray-200/50 opacity-60" : "border-gray-300/80 shadow-xs hover:shadow-sm"
                }`}
              >
                <div className="absolute left-0 top-0 h-full w-1" style={{ backgroundColor: entry.color }} />

                <div className="relative z-10 pl-1.5">
                  <div className="flex items-center justify-between mb-1">
                    <div className="w-2.5 h-2.5 rounded-sm border border-white/50" style={{ backgroundColor: entry.color }} />

                    <div
                      className="text-[10px] font-medium px-1 py-0.5 rounded-xl border backdrop-blur-sm"
                      style={{
                        backgroundColor: `${entry.color}15`,
                        color: entry.color,
                        borderColor: `${entry.color}25`,
                      }}
                    >
                      {percentage.toFixed(0)}%
                    </div>
                  </div>

                  <p className="text-base font-bold text-gray-900 mb-0">{displayValue}</p>

                  <p className="text-[14px] font-medium text-gray-600 uppercase tracking-tight">{entry.name}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Received Section */}
      {isRestaurant ? (
        // Restaurant: Show single Total Received
        <div className="bg-gradient-to-br from-emerald-50/80 to-teal-50/80 rounded-xl border border-emerald-200/50 p-3">
          <div className="flex items-center justify-between mb-2 pb-2 border-b border-emerald-200/30">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-5 bg-gradient-to-b from-emerald-500 to-teal-500 rounded-full"></div>
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider"> Total Received</span>
            </div>
            <span className="text-lg font-bold text-emerald-900">{formatAmount(totalReceived)}</span>
          </div>

          {/* Single combined received card */}
          <div className="bg-white/60 rounded-lg p-3 border border-emerald-200/40">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: "#10b981" }} />
                <span className="text-[11px] font-medium text-emerald-700">Total Received</span>
              </div>
              <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full"></span>
            </div>
            <p className="text-base font-bold text-emerald-900 pl-4">{formatAmount(totalReceived)}</p>
            <div className="mt-1 pl-4">
              <div className="w-full bg-emerald-100/50 rounded-full h-1">
                <div
                  className="h-1 rounded-full bg-emerald-500"
                  style={{
                    width: `${(totalReceived / totalWithReceived) * 100}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Non-Restaurant: Show split received sections
        <div className="bg-gradient-to-br from-emerald-50/80 to-teal-50/80 rounded-xl border border-emerald-200/50 p-3">
          {/* Total Received Header */}
          <div className="flex items-center justify-between mb-2 pb-2 border-b border-emerald-200/30">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-5 bg-gradient-to-b from-emerald-500 to-teal-500 rounded-full"></div>
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Total Received</span>
            </div>
            <span className="text-lg font-bold text-emerald-900">{formatAmount(totalReceived)}</span>
          </div>

          {/* Two Received Sections Side by Side */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {/* Order Received */}
            <div
              className={`bg-white/60 rounded-lg p-2.5 border ${
                (orderReceived?.value || 0) === 0 ? "border-gray-200/50 opacity-60" : "border-emerald-200/40 hover:shadow-sm"
              } transition-all duration-200`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: orderReceived?.color || "#10b981" }} />
                  <span className="text-[11px] font-medium text-emerald-700">Order Payment</span>
                </div>
                {/* <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">{paymentLabel}</span> */}
              </div>
              <p className="text-base font-bold text-emerald-900 pl-4">{formatAmount(orderReceived?.value || 0)}</p>
              <div className="mt-1 pl-4">
                <div className="w-full bg-emerald-100/50 rounded-full h-1">
                  <div
                    className="h-1 rounded-full bg-emerald-500"
                    style={{
                      width: `${((orderReceived?.value || 0) / totalWithReceived) * 100}%`,
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Ledger Received */}
            <div
              className={`bg-white/60 rounded-lg p-2.5 border ${(ledgerReceived?.value || 0) === 0 ? "border-gray-200/50 opacity-60" : "border-teal-200/40 hover:shadow-sm"} transition-all duration-200`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: ledgerReceived?.color || "#34d399" }} />
                  <span className="text-[11px] font-medium text-teal-700">Outstanding Settlement</span>
                </div>
                {/* <span className="text-xs text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded-full">{settlementLabel}</span> */}
              </div>
              <p className="text-base font-bold text-teal-900 pl-4">{formatAmount(ledgerReceived?.value || 0)}</p>
              <div className="mt-1 pl-4">
                <div className="w-full bg-teal-100/50 rounded-full h-1">
                  <div
                    className="h-1 rounded-full bg-teal-500"
                    style={{
                      width: `${((ledgerReceived?.value || 0) / totalWithReceived) * 100}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Outstanding Item - Displayed at the very end (only for non-Restaurant) */}
      {!isRestaurant && outstandingItem && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-2">
          <div
            className={`group relative overflow-hidden rounded-xl bg-gradient-to-br from-white to-red-50/80 border p-2 transition-all duration-200 ${
              (outstandingItem?.value ?? 0) <= 0 ? "border-gray-200/50 opacity-60" : "border-red-300/60 shadow-md hover:shadow-lg"
            }`}
          >
            <div className="absolute left-0 top-0 h-full w-1" style={{ backgroundColor: outstandingItem?.color || "#ef4444" }} />

            <div className="relative z-10 pl-1.5">
              <div className="flex items-center justify-between mb-1">
                <div className="w-2.5 h-2.5 rounded-sm border border-white/50" style={{ backgroundColor: outstandingItem?.color || "#ef4444" }} />

                <div
                  className="text-[10px] font-medium px-1 py-0.5 rounded-xl border backdrop-blur-sm"
                  style={{
                    backgroundColor: `${outstandingItem?.color || "#ef4444"}15`,
                    color: outstandingItem?.color || "#ef4444",
                    borderColor: `${outstandingItem?.color || "#ef4444"}25`,
                  }}
                >
                  {(((outstandingItem?.value || 0) / totalWithReceived) * 100).toFixed(0)}%
                </div>
              </div>

              <p className="text-base font-bold text-red-700 mb-0">{formatAmount(outstandingItem?.value || 0)}</p>

              <p className="text-[14px] font-medium text-red-700 uppercase tracking-tight">Outstanding</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataSummary;
